import React, { useState, useRef, useEffect, useCallback } from "react";
import { Form, Button, Overlay, Popover } from "react-bootstrap";
import { Mic, Paperclip, Pause, Send } from "react-bootstrap-icons";
import { InputProps } from "./input.props";
import { VoiceRecorderUI } from "./voice-recorder";
import { EmojiSmile } from "react-bootstrap-icons";
import { useVoiceRecorder } from "@/features/chat/model";
import { emojiCategories } from "@/features/chat/lib";
import { useMediaModal } from "@/shared/hooks";
import { MediaModal } from "@/shared/ui";
import { AttachedFilesPreview } from "./attached-files-preview";
import { useMediaQuery } from "react-responsive";

export const ChatMessagesInput = ({
  textareaRef,
  value,
  setValue,
  disabled,
  onSend,
  files,
  setFiles,
}: InputProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isMultiline, setIsMultiline] = useState(false);
  const isTouchDevice = useMediaQuery({ maxWidth: 1024 });

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const handleSendVoice = useCallback((file: File) => {
    onSend("", [file]);
  }, []);

  const [voiceState, voiceActions] = useVoiceRecorder(
    handleSendVoice,
    disabled
  );

  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);

  const { recording, recordTime, voiceBlob, recordingCanceled } = voiceState;

  const {
    modalMedia,
    currentIndex,
    openMediaModal,
    closeMediaModal,
    handlePrev,
    handleNext,
    isOpen,
    handleDelete,
  } = useMediaModal();

  const {
    startRecording,
    stopRecording,
    cancelRecording,
    resumeRecording,
    sendVoice,
    reset: resetVoice,
  } = voiceActions;

  const focusTextarea = () => {
    if (!isTouchDevice) {
      textareaRef.current?.focus();
    }
  };

  useEffect(() => {
    const timer = setTimeout(focusTextarea, 200);
    return () => clearTimeout(timer);
  }, [closeMediaModal]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const MAX_HEIGHT = 200;

    textarea.style.height = "auto";
    const newHeight = Math.min(textarea.scrollHeight, MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";

    const computed = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computed.lineHeight);
    const rows = Math.floor(textarea.scrollHeight / lineHeight);

    if (rows > 1 && !isMultiline) {
      setIsMultiline(true);
    }

    if (textarea.value === "" && isMultiline) {
      setIsMultiline(false);
    }
  }, [value]);

  useEffect(() => {
    if (textareaRef.current && !disabled && document.activeElement !== textareaRef.current) {
      focusTextarea();
    }
  }, [disabled, focusTextarea, textareaRef]);

  const handleSend = () => {
    if (disabled) return;

    const trimmed = value.trim();
    if (!trimmed && files.length === 0) return;

    onSend(trimmed, files);

    setValue("");
    // setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isTouchDevice) return;

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const removeFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;

    if (!selectedFiles) return;
    setFiles((prev) => [...prev, ...Array.from(selectedFiles)]);
    focusTextarea();
  };
  const handleEmojiClick = (emoji: string) => {
    setValue(value + emoji);
    focusTextarea();
  };

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    if (!items) return;

    const newFiles: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          const clonedFile = new File([file], file.name, { type: file.type });
          newFiles.push(clonedFile);
        }
      }
    }

    if (newFiles.length > 0) {
      e.preventDefault();
      setFiles(prev => [...prev, ...newFiles]);
      focusTextarea();
    }
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {files.length > 0 && (
        <AttachedFilesPreview
          files={files}
          currentIndex={currentIndex}
          onRemove={(index) => {
            if (index === -1) {
              setFiles([]);
            } else {
              removeFile(index);
            }
          }}
          onReplace={(index, newFile) => {
            setFiles((prev) =>
              prev.map((f, idx) => (idx === index ? newFile : f))
            );
          }}
          openMediaModal={openMediaModal}
        />
      )}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          border: "1px solid #ddd",
          borderRadius: 24,
          padding: "4px 8px",
          background: "#fff",
        }}
      >
        {/* Абсолютная кнопка СТОП или МИКРОФОН (продолжить запись) — показывается при записи или наличии blob */}
        {(recording || voiceBlob) && (
          <div
            style={{
              position: "absolute",
              top: -32,
              right: 15,
              zIndex: 10,
            }}
          >
            {recording ? (
              <Button
                variant="secondary"
                onClick={stopRecording}
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 24, height: 24, padding: 0 }}
                title="Остановить запись"
              >
                <Pause />
              </Button>
            ) : voiceBlob ? (
              <Button
                variant="secondary"
                onClick={resumeRecording}
                disabled={disabled}
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 24, height: 24, padding: 0 }}
                title="Продолжить запись"
              >
                <Mic />
              </Button>
            ) : null}
          </div>
        )}

        {recording || voiceBlob ? (
          <VoiceRecorderUI
            recording={recording}
            recordTime={recordTime}
            voiceBlob={voiceBlob}
            disabled={disabled}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            onCancelRecording={cancelRecording}
            onResumeRecording={resumeRecording}
            onSendVoice={sendVoice}
            onDiscardVoice={resetVoice}
          />
        ) : (
          <>
            <Button
              variant="outline-secondary"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 36, height: 36, padding: 0 }}
              title="Прикрепить файл"
            >
              <Paperclip />
            </Button>

            <Form.Control
              as="textarea"
              placeholder="Сообщение..."
              className="scrollbar-custom-y"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={1}
              disabled={disabled}
              ref={textareaRef}
              style={{
                resize: "none",
                border: "none",
                boxShadow: "none",
                padding: isMultiline ? "0 0 8px" : "8px 0",
                marginTop: isMultiline ? 4 : 0,
                minHeight: "42px",
                order: isMultiline ? -1 : 0,
                flex: isMultiline ? "1 0 100%" : "1",
                width: isMultiline ? "100%" : "auto",
              }}
            />

            <div style={{ marginLeft: "auto", order: isMultiline ? 1 : 0 }}>
              {value.trim() || files.length > 0 ? (
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    ref={emojiButtonRef}
                    disabled={disabled}
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 36, height: 36, padding: 0 }}
                    onMouseEnter={() => setShowEmojiPicker(true)}
                    title="Выбрать эмодзи"
                  >
                    <EmojiSmile />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleSend}
                    disabled={disabled}
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 36, height: 36, padding: 0 }}
                    title="Отправить"
                  >
                    <Send />
                  </Button>
                </div>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    ref={emojiButtonRef}
                    disabled={disabled}
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 36, height: 36, padding: 0 }}
                    onMouseEnter={() => setShowEmojiPicker(true)}
                    title="Выбрать эмодзи"
                  >
                    <EmojiSmile />
                  </Button>

                  <Button
                    variant="outline-secondary"
                    onClick={startRecording}
                    disabled={disabled}
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 36, height: 36, padding: 0 }}
                    title="Записать голосовое"
                  >
                    <Mic />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Overlay
        target={emojiButtonRef}
        show={showEmojiPicker}
        placement="top"
        rootClose
        onHide={() => setShowEmojiPicker(false)}
      >
        <Popover
          onMouseEnter={() => setShowEmojiPicker(true)}
          onMouseLeave={() => setShowEmojiPicker(false)}
          className="scrollbar-custom-y"
          style={{
            width: 226,
            maxHeight: 520,
            overflowY: "auto",
            padding: 0,
          }}
        >
          <Popover.Body style={{ padding: 8 }}>
            {Object.entries(emojiCategories).map(([category, emojis]) => (
              <div key={category} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                    paddingLeft: 2,
                  }}
                >
                  {category}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                  }}
                >
                  {emojis.map((emoji, index) => {
                    return (
                      <Button
                        key={category + "-" + emoji + "-" + index}
                        variant="light"
                        size="sm"
                        style={{
                          fontSize: 20,
                          padding: "2px 4px",
                          lineHeight: 1,
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={() => handleEmojiClick(emoji)}
                      >
                        {emoji}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </Popover.Body>
        </Popover>
      </Overlay>

      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <MediaModal
        isOpen={isOpen}
        media={modalMedia}
        currentIndex={currentIndex}
        onClose={closeMediaModal}
        onPrev={handlePrev}
        onNext={handleNext}
        onDelete={(i) => {
          removeFile(i);
          handleDelete(i);
        }}
      />
    </div>
  );
};
